import { MODES, type Mode, type Op } from "../content/modes";
import { RANKS } from "../content/ranks";
import { availableModes } from "./modes";

/*
  Card sets. Each strand has its own look of every ninja card (see
  content/variants.ts). A card turns gold when the child holds that rank in
  every strand in their set (the strands switched on, plus Mix), and foil when
  they hold it in all five. Gold and foil are dated when earned and never
  taken away. The overall ninja rank is the highest complete set, kept as a
  high-water mark.
*/

export type Ranks = Record<Mode, number>;
export type SetMap = Record<string, { gold?: string | undefined; foil?: string | undefined }>;

/** The strands a set needs: every strand switched on, plus Mix when more than one is on. */
export function setModes(ops: Record<Op, boolean>): Mode[] {
  return availableModes(ops);
}

/** Gold needs Mix, so a child playing a single strand has no gold cards. */
export function goldPossible(ops: Record<Op, boolean>): boolean {
  return setModes(ops).includes("mix");
}

/** The highest rank held in any strand switched on: the headline ninja on Home. */
export function highestRank(rank: Ranks, ops: Record<Op, boolean>): number {
  return Math.max(0, ...setModes(ops).map((m) => rank[m]));
}

/** The highest rank held in every strand of the set. */
export function completeRank(rank: Ranks, ops: Record<Op, boolean>): number {
  return Math.min(...setModes(ops).map((m) => rank[m]));
}

export function goldReady(rank: Ranks, ops: Record<Op, boolean>, rankIndex: number): boolean {
  return rankIndex >= 1 && goldPossible(ops) && completeRank(rank, ops) >= rankIndex;
}

export function foilReady(rank: Ranks, rankIndex: number): boolean {
  return rankIndex >= 1 && MODES.every((m) => rank[m] >= rankIndex);
}

export interface SetUpdate {
  sets: SetMap;
  overall: number;
  /** Cards that turned gold or foil just now. */
  newGold: number[];
  newFoil: number[];
}

/**
 * Bring sets and the overall rank up to date with the ranks held. Only ever
 * adds: nothing already earned is taken away. Pure.
 */
export function updateSets(
  s: { rank: Ranks; ops: Record<Op, boolean>; sets: SetMap; overall: number },
  today: string,
): SetUpdate {
  const sets: SetMap = { ...s.sets };
  const newGold: number[] = [];
  const newFoil: number[] = [];
  for (let i = 1; i < RANKS.length; i++) {
    const key = String(i);
    const had = sets[key] ?? {};
    const gold = !had.gold && goldReady(s.rank, s.ops, i);
    const foil = !had.foil && foilReady(s.rank, i);
    if (gold) newGold.push(i);
    if (foil) newFoil.push(i);
    if (gold || foil)
      sets[key] = { ...had, ...(gold ? { gold: today } : {}), ...(foil ? { foil: today } : {}) };
  }
  const overall = Math.max(s.overall, completeRank(s.rank, s.ops));
  return { sets, overall, newGold, newFoil };
}

export type Frame = "foil" | "gold" | null;

export function frameFor(sets: SetMap, rankIndex: number): Frame {
  const s = sets[String(rankIndex)];
  return s?.foil ? "foil" : s?.gold ? "gold" : null;
}

/** Which strands hold `rankIndex`, for the dots under a card. */
export function setProgress(
  rank: Ranks,
  ops: Record<Op, boolean>,
  rankIndex: number,
): { mode: Mode; held: boolean }[] {
  return setModes(ops).map((mode) => ({ mode, held: rank[mode] >= rankIndex }));
}

/** Showiest first: the default look for a card is the best variant held. */
const LOOK_ORDER: readonly Mode[] = ["mix", "div", "mul", "sub", "add"];

/** Whether the variant of `rankIndex` for `mode` is held. */
export function holdsVariant(rank: Ranks, mode: Mode, rankIndex: number): boolean {
  return rank[mode] >= rankIndex;
}

/**
 * The look a card shows on Home: the child's choice if they still hold it,
 * else the showiest variant held among the strands they play (Classic at Seedling).
 */
export function lookFor(
  rank: Ranks,
  looks: Partial<Record<string, Mode>>,
  rankIndex: number,
  modes: readonly Mode[],
): Mode {
  const chosen = looks[String(rankIndex)];
  if (chosen && holdsVariant(rank, chosen, rankIndex)) return chosen;
  // Seedling is where everyone starts: Classic, so no look suggests an achievement.
  if (rankIndex === 0) return "add";
  return (
    LOOK_ORDER.find((m) => modes.includes(m) && holdsVariant(rank, m, rankIndex)) ??
    modes[0] ??
    "add"
  );
}
